"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import type { PublicCatalogItemDetail } from "@bash/shared";
import { getPublicCatalogItem } from "@/lib/api/catalog";
import { Button } from "@/components/ui/button";
import Masthead from "@/components/atoms/masthead";

interface PublicDetailViewProps {
  /** Plural URL segment: 'skills' | 'agents' */
  type: string;
  slug: string;
}
function slugToTitle(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
function cleanName(name: string, slug: string): string {
  const n = name || slug;
  if (n.includes("-") && !n.includes(" ")) return slugToTitle(n);
  return n;
}

/**
 * Public (anon) detail view for a catalog item (skill or agent).
 *
 * Fetches the item via the no-auth catalog API and renders:
 *   - loading  → skeleton
 *   - null     → branded not-found with back link
 *   - item     → header (name, category, version), "Sign in to install" CTA,
 *                and the item content in a readable prose block
 */
export function PublicDetailView({ type, slug }: PublicDetailViewProps) {
  const [item, setItem] = useState<PublicCatalogItemDetail | null | undefined>(
    undefined // undefined = still loading; null = not found
  );

  useEffect(() => {
    let alive = true;
    getPublicCatalogItem(type, slug)
      .then((d) => {
        if (alive) setItem(d);
      })
      .catch(() => {
        if (alive) setItem(null);
      });
    return () => {
      alive = false;
    };
  }, [type, slug]);

  // Loading state
  if (item === undefined) {
    return (
      <div>
        <div className="h-8 w-48 animate-pulse-subtle-1 rounded-12 bg-bg-muted mb-4" />
        <div className="h-4 w-72 animate-pulse-subtle-1 rounded-12 bg-bg-muted mb-8" />
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-4 w-full animate-pulse-subtle-1 rounded-12 bg-bg-muted"
            />
          ))}
        </div>
      </div>
    );
  }

  // Not-found state
  if (item === null) {
    return (
      <div>
        <h1 className="text-6 font-semibold text-fg-default">
          We couldn&apos;t find that
        </h1>
        <p className="mt-2 text-3.5 text-fg-muted">
          This item may have been removed or the URL is incorrect.
        </p>
        <Button asChild>
          <Link href={`/${type}`} className="mt-6">
            Back to {type.charAt(0).toUpperCase() + type.slice(1)}
          </Link>
        </Button>
      </div>
    );
  }

  const loginHref = `/login?next=${encodeURIComponent(
    "/" + type + "/" + slug
  )}`;

  return (
    <>
      <header className="">
        <Masthead
          eyebrow={item.category || undefined}
          title={cleanName(item.name, slug)}
          subtitle={item.description}
          actions={
            <>
              {item.version != null && (
                <span className="text-3 text-fg-subtle">v{item.version}</span>
              )}
              <Button asChild>
                <Link href={loginHref}>Sign in to install</Link>
              </Button>
            </>
          }
        />
      </header>

      {/* Content */}
      <div className="rounded-10 sm:rounded-16 border border-border-subtle bg-bg-elevated p-4 sm:p-5">
        <pre className="whitespace-pre-wrap break-words font-sans text-3.5 leading-relaxed text-fg-default">
          {item.content}
        </pre>
      </div>
    </>
  );
}
